import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Autocomplete, Chip, Alert,
  Divider, Pagination, LinearProgress, Collapse, Tooltip,
  CircularProgress, Stack, Select, MenuItem, FormControl,
  InputLabel, FormHelperText, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AccountTree as TreeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayArrowIcon,
  Warning as WarningIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Build as BuildIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  Route as RouteIcon,
  ArrowForward as ArrowForwardIcon,
  CallSplit as PhantomIcon,
  Settings as SettingsIcon,
  DragIndicator as DragIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { useManufacturingData } from '../../contexts/ManufacturingDataContext';
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getItemTypeColor = (type) => {
  const colors = {
    'raw_material': 'primary',
    'manufactured': 'secondary',
    'assembly': 'warning',
    'finished_good': 'success'
  };
  return colors[type] || 'default';
};

const getItemTypeLabel = (type) => {
  const labels = {
    'raw_material': 'Raw',
    'manufactured': 'Mfg',
    'assembly': 'Assy',
    'finished_good': 'FG'
  };
  return labels[type] || type;
};

const parseItemDisplay = (display) => {
  if (!display) return { code: '', name: '' };
  const parts = display.split(' - ');
  return {
    code: parts[0] || '',
    name: parts.slice(1).join(' - ') || ''
  };
};

const computeRootParents = (allBoms) => {
  const parentItems = new Set();
  const componentItems = new Set();

  allBoms.forEach(bom => {
    if (bom.parent_item_display) {
      parentItems.add(bom.parent_item_display);
    }
    if (bom.child_item_display) {
      componentItems.add(bom.child_item_display);
    }
  });

  const rootParents = new Set();
  parentItems.forEach(parent => {
    if (!componentItems.has(parent)) {
      rootParents.add(parent);
    }
  });

  return rootParents;
};

const getItemsWithChildren = (allBoms) => {
  return new Set(allBoms.map(b => b.parent_item_display));
};

// ============================================================================
// ROUTING STEP FLOW VISUALIZATION
// ============================================================================

const RoutingStepFlow = ({ routingSteps, onManageRouting }) => {
  if (!routingSteps || routingSteps.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={<RouteIcon />}
          label="Single-step (legacy)"
          size="small"
          variant="outlined"
          color="default"
        />
        <Tooltip title="Configure multi-step routing">
          <IconButton size="small" onClick={onManageRouting}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title="Configure routing">
        <IconButton size="small" onClick={onManageRouting} sx={{ mr: 0.5 }}>
          <RouteIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
      {routingSteps.map((step, index) => (
        <React.Fragment key={step.id || index}>
          <Tooltip title={step.description || `Step ${step.sequence}`}>
            <Chip
              label={`${step.sequence}. ${step.operation_code || step.operation?.operation_code || 'OP'}`}
              size="small"
              color={index === 0 ? 'primary' : index === routingSteps.length - 1 ? 'success' : 'default'}
              variant={index === routingSteps.length - 1 ? 'filled' : 'outlined'}
              sx={{ 
                fontWeight: 'bold',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Tooltip>
          {index < routingSteps.length - 1 && (
            <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

// ============================================================================
// ENHANCED ITEM SELECTOR
// ============================================================================

const EnhancedItemSelector = ({
  label,
  value,
  onChange,
  helperText,
  filterTypes = null,
  excludeItems = [],
  required = false,
  size = "medium",
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchError, setSearchError] = useState(null);

  useEffect(() => {
    if (value) {
      setInputValue(`${value.item_code} - ${value.name}`);
      setSearchError(null);
    } else {
      setInputValue('');
    }
  }, [value]);

  const searchItems = useCallback(async (search) => {
    if (search.length < 2) {
      setFilteredItems([]);
      setSearchError(null);
      return;
    }

    const truncatedSearch = search.substring(0, 100);
    setLoading(true);
    setSearchError(null);

    try {
      const params = {
        search: truncatedSearch,
        page_size: 50,
        ...(filterTypes && { type: filterTypes.join(',') })
      };
      const response = await manufacturingAPI.items.list(params);
      const filtered = response.data.results.filter(
        item => !excludeItems.includes(item.id)
      );
      setFilteredItems(filtered);

      if (filtered.length === 0 && truncatedSearch.length >= 2) {
        setSearchError(`No items found matching "${truncatedSearch}"`);
      }
    } catch (error) {
      console.error('Error searching items:', error);
      setSearchError('Error searching items');
      setFilteredItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterTypes, excludeItems]);

  const handleInputChange = useCallback((event, newInputValue, reason) => {
    if (reason === 'reset') return;

    setInputValue(newInputValue);
    setSearchError(null);

    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      if (newInputValue && newInputValue.length >= 2) {
        searchItems(newInputValue);
      } else {
        setFilteredItems([]);
      }
    }, 400);

    setSearchTimeout(timeout);
  }, [searchItems, searchTimeout]);

  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  return (
    <Autocomplete
      options={filteredItems}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return `${option.item_code} - ${option.name}`;
      }}
      value={value}
      inputValue={inputValue}
      onChange={(event, newValue) => {
        onChange(newValue);
        if (newValue) {
          setFilteredItems([]);
          setSearchError(null);
        }
      }}
      onInputChange={handleInputChange}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      size={size}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${label}${required ? ' *' : ''}`}
          helperText={searchError || helperText}
          size={size}
          disabled={disabled}
          error={!!searchError}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <Box component="li" key={key} {...otherProps}>
            <Stack spacing={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {option.item_code}
                </Typography>
                <Chip
                  label={getItemTypeLabel(option.item_type)}
                  size="small"
                  color={getItemTypeColor(option.item_type)}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
                {option.is_phantom && (
                  <Chip
                    icon={<PhantomIcon sx={{ fontSize: 12 }} />}
                    label="Phantom"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.name}
              </Typography>
            </Stack>
          </Box>
        );
      }}
      noOptionsText={
        loading ? "Searching..." :
        inputValue.length < 2 ? "Type 2+ characters" :
        "No items found"
      }
    />
  );
};

// ============================================================================
// ITEM DISPLAY COMPONENT
// ============================================================================

const ItemDisplay = ({ display, showIcon = false, hasChildren = false, isPhantom = false }) => {
  const { code, name } = parseItemDisplay(display);
  
  return (
    <Stack spacing={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isPhantom && (
          <Tooltip title="Phantom - components explode through to parent">
            <PhantomIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          </Tooltip>
        )}
        {showIcon && hasChildren && !isPhantom && (
          <TreeIcon sx={{ fontSize: 16, color: 'warning.main' }} />
        )}
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          {code}
        </Typography>
        {isPhantom && (
          <Chip
            label="Phantom"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
          />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {name}
      </Typography>
    </Stack>
  );
};

// ============================================================================
// STATS CARD
// ============================================================================

const StatsCard = ({ title, value, color = 'primary', icon: Icon }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Icon && <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />}
        <Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" color={`${color}.main`} fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ============================================================================
// STEP BADGE COMPONENT
// ============================================================================

const StepBadge = ({ step, totalSteps }) => {
  if (!totalSteps || totalSteps <= 1) return null;
  
  return (
    <Tooltip title={`Consumed in Step ${step} of ${totalSteps}`}>
      <Chip
        label={`S${step}`}
        size="small"
        color={step === 1 ? 'primary' : step === totalSteps ? 'success' : 'default'}
        variant="outlined"
        sx={{ 
          height: 20, 
          fontSize: '0.65rem',
          fontWeight: 'bold',
          minWidth: 28
        }}
      />
    </Tooltip>
  );
};

// ============================================================================
// INLINE STEP SELECTOR
// ============================================================================

const InlineStepSelector = ({ value, totalSteps, routingSteps, onChange }) => {
  const [updating, setUpdating] = useState(false);

  const handleChange = async (e) => {
    const newValue = parseInt(e.target.value);
    if (newValue === value) return;
    
    setUpdating(true);
    try {
      await onChange(newValue);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <FormControl size="small" sx={{ minWidth: 50 }}>
      <Select
        value={value}
        onChange={handleChange}
        disabled={updating}
        sx={{ 
          fontSize: '0.75rem',
          '& .MuiSelect-select': { py: 0.5, px: 1 }
        }}
      >
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((stepNum) => {
          const stepInfo = routingSteps?.find(r => r.sequence === stepNum);
          return (
            <MenuItem key={stepNum} value={stepNum}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" fontWeight="bold">S{stepNum}</Typography>
                {stepInfo && (
                  <Typography variant="caption" color="text.secondary">
                    {stepInfo.operation_code || ''}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

// ============================================================================
// NESTED BOM TREE COMPONENT
// ============================================================================

function NestedBOMTree({
  parentItemDisplay,
  allBoms,
  itemsWithChildren,
  level = 0,
  onEdit,
  onDelete,
  onEditAsParent,
  onTogglePhantom,
  onDuplicate,
  maxDepth = 5,
  visitedItems = new Set(),
  routingSteps = []
}) {
  if (visitedItems.has(parentItemDisplay) || level >= maxDepth) {
    return null;
  }

  const newVisitedItems = new Set([...visitedItems, parentItemDisplay]);

  const children = allBoms.filter(bom =>
    bom.parent_item_display === parentItemDisplay
  );

  if (children.length === 0) return null;

  const totalSteps = routingSteps.length || 1;

  return (
    <Box sx={{ ml: 3, borderLeft: '2px solid', borderColor: 'grey.300', pl: 1 }}>
      {children
        .sort((a, b) => {
          // Sort by step first, then by item code
          const stepDiff = (a.consumed_in_step || 1) - (b.consumed_in_step || 1);
          if (stepDiff !== 0) return stepDiff;
          return a.child_item_display.localeCompare(b.child_item_display);
        })
        .map((bomItem) => {
          const hasGrandchildren = itemsWithChildren.has(bomItem.child_item_display);
          const childAssemblyOp = bomItem.child_assembly_operation_display;
          const isPhantom = bomItem.child_is_phantom;

          return (
            <Box key={bomItem.id} sx={{ py: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: isPhantom ? 'warning.50' : 'grey.50',
                  borderLeft: isPhantom ? '3px solid' : 'none',
                  borderColor: 'warning.main',
                  '&:hover': { bgcolor: isPhantom ? 'warning.100' : 'grey.100' }
                }}
              >
                <StepBadge step={bomItem.consumed_in_step || 1} totalSteps={totalSteps} />
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ItemDisplay 
                    display={bomItem.child_item_display} 
                    showIcon={true}
                    hasChildren={hasGrandchildren}
                    isPhantom={isPhantom}
                  />
                  {hasGrandchildren && childAssemblyOp && !isPhantom && (
                    <Chip
                      icon={<BuildIcon sx={{ fontSize: 12 }} />}
                      label={childAssemblyOp}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                    />
                  )}
                </Box>

                <Box sx={{ width: 70, textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight="bold">
                    x{parseFloat(bomItem.quantity_required)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {hasGrandchildren && (
                    <Tooltip title={isPhantom ? "Unmark as Phantom" : "Mark as Phantom"}>
                      <IconButton 
                        size="small" 
                        color={isPhantom ? "warning" : "default"}
                        onClick={() => onTogglePhantom && onTogglePhantom(bomItem.child_item, !isPhantom)}
                      >
                        <PhantomIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasGrandchildren && !isPhantom && (
                    <Tooltip title="Edit this item's BOM & operation">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => onEditAsParent(bomItem.child_item, bomItem.child_item_display)}
                      >
                        <BuildIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasGrandchildren && onDuplicate && (
                    <Tooltip title="Duplicate this item's BOM">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const { code, name } = parseItemDisplay(bomItem.child_item_display);
                          onDuplicate({
                            id: bomItem.child_item,
                            item_code: code,
                            name: name,
                            item_type: bomItem.child_item_type || 'manufactured',
                            is_phantom: bomItem.child_is_phantom || false,
                          });
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(bomItem)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onDelete(bomItem)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Show phantom's children as "exploded through" */}
              {isPhantom && hasGrandchildren && (
                <Box sx={{ ml: 2, mt: 0.5, pl: 1, borderLeft: '2px dashed', borderColor: 'warning.light' }}>
                  <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                    Explodes to:
                  </Typography>
                  <NestedBOMTree
                    parentItemDisplay={bomItem.child_item_display}
                    allBoms={allBoms}
                    itemsWithChildren={itemsWithChildren}
                    level={level + 1}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onEditAsParent={onEditAsParent}
                    onTogglePhantom={onTogglePhantom}
                    onDuplicate={onDuplicate}
                    maxDepth={maxDepth}
                    visitedItems={newVisitedItems}
                    routingSteps={[]}
                  />
                </Box>
              )}

              {/* Regular sub-assembly children */}
              {hasGrandchildren && !isPhantom && (
                <NestedBOMTree
                  parentItemDisplay={bomItem.child_item_display}
                  allBoms={allBoms}
                  itemsWithChildren={itemsWithChildren}
                  level={level + 1}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onEditAsParent={onEditAsParent}
                  onTogglePhantom={onTogglePhantom}
                  onDuplicate={onDuplicate}
                  maxDepth={maxDepth}
                  visitedItems={newVisitedItems}
                  routingSteps={[]}
                />
              )}
            </Box>
          );
        })}
    </Box>
  );
}

// ============================================================================
// ROUTING MANAGEMENT DIALOG
// ============================================================================

const RoutingDialog = ({ open, onClose, item, operations, onSave }) => {
  const [routingSteps, setRoutingSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemData, setItemData] = useState(null);

  useEffect(() => {
    if (open && item) {
      loadRouting();
    } else if (!open) {
      setRoutingSteps([]);
      setItemData(null);
    }
  }, [open, item]);

  const loadRouting = async () => {
    if (!item) return;
    setLoading(true);
    try {
      // First, fetch fresh item data to get assembly_operation
      let freshItem = item;
      try {
        const itemResponse = await manufacturingAPI.items.get(item.id);
        freshItem = itemResponse.data;
        setItemData(freshItem);
      } catch (err) {
        console.warn('Could not fetch fresh item data:', err);
        setItemData(item);
      }

      // Then fetch routing steps
      const response = await manufacturingAPI.itemRouting.list({ item: item.id });
      const steps = response.data.results || response.data || [];
      
      if (steps.length > 0) {
        setRoutingSteps(steps.sort((a, b) => a.sequence - b.sequence));
      } else if (freshItem.assembly_operation) {
        // Initialize with single step from assembly_operation
        const operation = operations.find(op => op.id === freshItem.assembly_operation);
        setRoutingSteps([{
          id: null,
          item: item.id,
          operation: freshItem.assembly_operation,
          operation_display: operation ? `${operation.operation_code} - ${operation.name}` : null,
          sequence: 1,
          description: 'Default step',
          estimated_time_minutes: 0,
          is_active: true
        }]);
      } else {
        // No routing and no assembly_operation - start with empty step for user to fill
        setRoutingSteps([{
          id: null,
          item: item.id,
          operation: null,
          sequence: 1,
          description: '',
          estimated_time_minutes: 0,
          is_active: true
        }]);
      }
    } catch (error) {
      console.error('Error loading routing:', error);
      toast.error('Failed to load routing');
      // Still allow user to add steps even on error
      setRoutingSteps([{
        id: null,
        item: item.id,
        operation: null,
        sequence: 1,
        description: '',
        estimated_time_minutes: 0,
        is_active: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    const nextSequence = routingSteps.length > 0 
      ? Math.max(...routingSteps.map(s => s.sequence)) + 1 
      : 1;
    setRoutingSteps([...routingSteps, {
      id: null,
      item: item.id,
      operation: null,
      sequence: nextSequence,
      description: '',
      estimated_time_minutes: 0,
      is_active: true
    }]);
  };

  const removeStep = (index) => {
    if (routingSteps.length <= 1) {
      toast.warning('At least one step is required');
      return;
    }
    const newSteps = routingSteps.filter((_, i) => i !== index);
    // Re-sequence
    newSteps.forEach((step, i) => {
      step.sequence = i + 1;
    });
    setRoutingSteps(newSteps);
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...routingSteps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setRoutingSteps(newSteps);
  };

  const moveStep = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === routingSteps.length - 1)
    ) return;

    const newSteps = [...routingSteps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index + direction];
    newSteps[index + direction] = temp;
    
    // Re-sequence
    newSteps.forEach((step, i) => {
      step.sequence = i + 1;
    });
    setRoutingSteps(newSteps);
  };

  const handleSave = async () => {
    // Validate
    const invalidSteps = routingSteps.filter(s => !s.operation);
    if (invalidSteps.length > 0) {
      toast.error('All steps must have an operation selected');
      return;
    }

    setSaving(true);
    try {
      // Get existing routing to determine what to delete/update/create
      const existingResponse = await manufacturingAPI.itemRouting.list({ item: item.id });
      const existingSteps = existingResponse.data.results || existingResponse.data || [];
      const existingIds = new Set(existingSteps.map(s => s.id));
      const newIds = new Set(routingSteps.filter(s => s.id).map(s => s.id));

      // Delete removed steps
      for (const existing of existingSteps) {
        if (!newIds.has(existing.id)) {
          await manufacturingAPI.itemRouting.delete(existing.id);
        }
      }

      // Phase 1: park kept steps at temporary sequences. Sequences are unique
      // per item, so writing final values one-by-one after a reorder would
      // collide with a step still holding that sequence and abort the save.
      const keptSteps = routingSteps.filter(s => s.id && existingIds.has(s.id));
      for (const step of keptSteps) {
        await manufacturingAPI.itemRouting.update(step.id, { sequence: step.sequence + 1000 });
      }

      // Phase 2: update or create steps with their final data
      for (const step of routingSteps) {
        const stepData = {
          item: item.id,
          operation: typeof step.operation === 'object' ? step.operation.id : step.operation,
          sequence: step.sequence,
          description: step.description || '',
          estimated_time_minutes: step.estimated_time_minutes || 0,
          is_active: true
        };

        if (step.id && existingIds.has(step.id)) {
          await manufacturingAPI.itemRouting.update(step.id, stepData);
        } else {
          await manufacturingAPI.itemRouting.create(stepData);
        }
      }

      // Update item's assembly_operation to first step's operation (for backward compatibility)
      if (routingSteps.length > 0) {
        const firstOp = routingSteps[0].operation;
        const opId = typeof firstOp === 'object' ? firstOp.id : firstOp;
        await manufacturingAPI.items.update(item.id, { assembly_operation: opId });
      }

      toast.success('Routing saved successfully');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving routing:', error);
      toast.error('Failed to save routing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon color="primary" />
          <span>Manufacturing Routing: {itemData?.item_code || item?.item_code || 'Loading...'}</span>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Define the sequence of operations to manufacture this item. Components in the BOM can be assigned to specific steps.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {itemData?.name || item?.name || ''}
              </Typography>
            </Box>

            {/* Step headers */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                px: 1,
                py: 0.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
                mb: 1
              }}
            >
              <Box sx={{ width: 40 }} />
              <Box sx={{ width: 50 }}>
                <Typography variant="caption" fontWeight="bold">SEQ</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" fontWeight="bold">OPERATION *</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" fontWeight="bold">DESCRIPTION</Typography>
              </Box>
              <Box sx={{ width: 100 }}>
                <Typography variant="caption" fontWeight="bold">TIME (min)</Typography>
              </Box>
              <Box sx={{ width: 80 }} />
            </Box>

            {routingSteps.map((step, index) => (
              <Card key={step.id || `new-${index}`} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box sx={{ width: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <IconButton 
                      size="small" 
                      onClick={() => moveStep(index, -1)}
                      disabled={index === 0}
                    >
                      <ExpandLessIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => moveStep(index, 1)}
                      disabled={index === routingSteps.length - 1}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ width: 50 }}>
                    <Chip 
                      label={step.sequence} 
                      size="small" 
                      color={index === 0 ? 'primary' : index === routingSteps.length - 1 ? 'success' : 'default'}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={operations}
                      getOptionLabel={(op) => typeof op === 'object' ? `${op.operation_code} - ${op.name}` : ''}
                      value={operations.find(op => op.id === (typeof step.operation === 'object' ? step.operation?.id : step.operation)) || null}
                      onChange={(e, val) => updateStep(index, 'operation', val?.id || null)}
                      size="small"
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select operation" size="small" />
                      )}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Step description"
                      value={step.description || ''}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                    />
                  </Box>

                  <Box sx={{ width: 100 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={step.estimated_time_minutes || ''}
                      onChange={(e) => updateStep(index, 'estimated_time_minutes', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                    />
                  </Box>

                  <Box sx={{ width: 80, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeStep(index)}
                      disabled={routingSteps.length <= 1}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}

            <Button
              startIcon={<AddCircleIcon />}
              onClick={addStep}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            >
              Add Step
            </Button>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || routingSteps.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : 'Save Routing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// PARENT BOM CARD
// ============================================================================

function ParentBOMCard({
  parentKey,
  bomItems,
  allBoms,
  itemsWithChildren,
  isRootParent,
  parentItem,
  isExpanded,
  onToggle,
  onEdit,
  onEditBulk,
  onDelete,
  onExplode,
  onEditAsParent,
  onManageRouting,
  onTogglePhantom,
  onDuplicate,
  onUpdateStep,
  routingSteps = []
}) {
  const { code: parentCode, name: parentName } = parseItemDisplay(parentKey);
  const parentAssemblyOp = bomItems[0]?.parent_assembly_operation_display;
  const totalSteps = routingSteps.length || 1;

  // Get parent_is_phantom from BOM data (more reliable than parentItem from context)
  const parentIsPhantom = bomItems[0]?.parent_is_phantom || parentItem?.is_phantom || false;

  // Group BOM items by step for display
  const bomsByStep = useMemo(() => {
    const grouped = {};
    bomItems.forEach(bom => {
      const step = bom.consumed_in_step || 1;
      if (!grouped[step]) grouped[step] = [];
      grouped[step].push(bom);
    });
    return grouped;
  }, [bomItems]);

  return (
    <Card sx={{ mb: 2 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: 'pointer',
          bgcolor: isExpanded ? 'primary.50' : 'transparent',
          borderBottom: isExpanded ? '1px solid' : 'none',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <Box sx={{ color: 'text.secondary' }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TreeIcon color="primary" sx={{ mt: 0.25 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="h6" fontWeight="bold">
                  {parentCode}
                </Typography>
                {parentIsPhantom && (
                  <Chip
                    icon={<PhantomIcon sx={{ fontSize: 14 }} />}
                    label="Phantom"
                    size="small"
                    color="warning"
                  />
                )}
                {isRootParent && (
                  <Chip
                    label="Root"
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {parentName}
              </Typography>
              
              {/* Routing flow */}
              <Box onClick={(e) => e.stopPropagation()}>
                <RoutingStepFlow 
                  routingSteps={routingSteps} 
                  onManageRouting={() => onManageRouting(parentItem || { id: bomItems[0]?.parent_item })}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Chip
          label={`${bomItems.length} components`}
          size="small"
          variant="outlined"
        />

        <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={parentIsPhantom ? "Unmark as Phantom" : "Mark as Phantom"}>
            <IconButton 
              size="small" 
              color={parentIsPhantom ? "warning" : "default"}
              onClick={() => onTogglePhantom(parentItem?.id || bomItems[0]?.parent_item, !parentIsPhantom)}
            >
              <PhantomIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit All Components">
            <IconButton size="small" color="primary" onClick={() => onEditBulk(bomItems)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Explode BOM">
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={() => onExplode(parentItem)}
              disabled={!parentItem}
            >
              Explode
            </Button>
          </Tooltip>
          <Tooltip title="Duplicate BOM to new item">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<ContentCopyIcon />}
              onClick={() => onDuplicate(parentItem || { id: bomItems[0]?.parent_item, item_code: parentCode, name: parentName })}
            >
              Duplicate
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {/* Column headers */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              px: 1.5,
              bgcolor: 'grey.100',
              borderRadius: 1,
              mb: 1
            }}
          >
            {totalSteps > 1 && (
              <Box sx={{ width: 50 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  STEP
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                COMPONENT
              </Typography>
            </Box>
            <Box sx={{ width: 70, textAlign: 'center' }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                QTY
              </Typography>
            </Box>
            <Box sx={{ width: 170 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                ACTIONS
              </Typography>
            </Box>
          </Box>

          {/* Render by step if multi-step */}
          {totalSteps > 1 ? (
            Object.entries(bomsByStep)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([stepNum, stepBoms]) => {
                const stepInfo = routingSteps.find(r => r.sequence === parseInt(stepNum));
                return (
                  <Box key={stepNum} sx={{ mb: 2 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mb: 1,
                      py: 0.5,
                      px: 1,
                      bgcolor: 'primary.50',
                      borderRadius: 1
                    }}>
                      <Chip 
                        label={`Step ${stepNum}`} 
                        size="small" 
                        color={parseInt(stepNum) === totalSteps ? 'success' : 'primary'}
                      />
                      {stepInfo && (
                        <Typography variant="caption" color="text.secondary">
                          {stepInfo.operation_code || stepInfo.operation?.operation_code} - {stepInfo.description}
                        </Typography>
                      )}
                    </Box>
                    {renderBomItems(stepBoms, totalSteps)}
                  </Box>
                );
              })
          ) : (
            renderBomItems(bomItems, totalSteps)
          )}
        </Box>
      </Collapse>
    </Card>
  );

  function renderBomItems(items, totalSteps) {
    return items
      .sort((a, b) => a.child_item_display.localeCompare(b.child_item_display))
      .map((bomItem) => {
        const hasChildren = itemsWithChildren.has(bomItem.child_item_display);
        const childAssemblyOp = bomItem.child_assembly_operation_display;
        const isPhantom = bomItem.child_is_phantom;

        return (
          <Box key={bomItem.id}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                px: 1.5,
                borderLeft: '3px solid',
                borderColor: isPhantom ? 'warning.main' : hasChildren ? 'warning.main' : 'primary.main',
                bgcolor: isPhantom ? 'warning.50' : 'background.paper',
                '&:hover': { bgcolor: isPhantom ? 'warning.100' : 'grey.50' }
              }}
            >
              {totalSteps > 1 && (
                <Box sx={{ width: 50 }}>
                  <InlineStepSelector
                    value={bomItem.consumed_in_step || 1}
                    totalSteps={totalSteps}
                    routingSteps={routingSteps}
                    onChange={(newStep) => onUpdateStep(bomItem, newStep)}
                  />
                </Box>
              )}
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ItemDisplay 
                  display={bomItem.child_item_display}
                  showIcon={true}
                  hasChildren={hasChildren}
                  isPhantom={isPhantom}
                />
                {hasChildren && childAssemblyOp && !isPhantom && (
                  <Chip
                    icon={<BuildIcon sx={{ fontSize: 12 }} />}
                    label={childAssemblyOp}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ mt: 0.5, height: 20 }}
                  />
                )}
              </Box>

              <Box sx={{ width: 70, textAlign: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  x{parseFloat(bomItem.quantity_required)}
                </Typography>
              </Box>

              <Box sx={{ width: 170, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {hasChildren && (
                  <Tooltip title={isPhantom ? "Unmark as Phantom" : "Mark as Phantom (explodes through)"}>
                    <IconButton
                      size="small"
                      color={isPhantom ? "warning" : "default"}
                      onClick={() => onTogglePhantom(bomItem.child_item, !isPhantom)}
                    >
                      <PhantomIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {hasChildren && !isPhantom && (
                  <Tooltip title="Edit this item's BOM & operation">
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => onEditAsParent(bomItem.child_item, bomItem.child_item_display)}
                    >
                      <BuildIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {hasChildren && (
                  <Tooltip title="Duplicate this item's BOM">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const { code, name } = parseItemDisplay(bomItem.child_item_display);
                        onDuplicate({
                          id: bomItem.child_item,
                          item_code: code,
                          name: name,
                          item_type: bomItem.child_item_type || 'manufactured',
                          is_phantom: bomItem.child_is_phantom || false,
                        });
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(bomItem)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => onDelete(bomItem)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Phantom explosion display */}
            {isPhantom && hasChildren && (
              <Box sx={{ ml: 4, mt: 0.5, pl: 1, borderLeft: '2px dashed', borderColor: 'warning.light' }}>
                <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                  Explodes to:
                </Typography>
                <NestedBOMTree
                  parentItemDisplay={bomItem.child_item_display}
                  allBoms={allBoms}
                  itemsWithChildren={itemsWithChildren}
                  level={1}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onEditAsParent={onEditAsParent}
                  onTogglePhantom={onTogglePhantom}
                  onDuplicate={onDuplicate}
                  routingSteps={[]}
                />
              </Box>
            )}

            {/* Regular sub-assembly tree */}
            {hasChildren && !isPhantom && (
              <NestedBOMTree
                parentItemDisplay={bomItem.child_item_display}
                allBoms={allBoms}
                itemsWithChildren={itemsWithChildren}
                level={1}
                onEdit={onEdit}
                onDelete={onDelete}
                onEditAsParent={onEditAsParent}
                onTogglePhantom={onTogglePhantom}
                onDuplicate={onDuplicate}
                routingSteps={[]}
              />
            )}
          </Box>
        );
      });
  }
}

// ============================================================================
// BOM DIALOG
// ============================================================================

const BOMDialog = ({
  open,
  onClose,
  editingBom,
  editingBulkBoms,
  onSave,
  items,
  operations
}) => {
  const [formData, setFormData] = useState({
    parent_item: null,
    assembly_operation: null,
    effective_date: new Date().toISOString().split('T')[0],
    components: [{ id: null, child_item: null, quantity_required: '', sequence: 1, consumed_in_step: 1 }]
  });

  const [saving, setSaving] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [originalAssemblyOperation, setOriginalAssemblyOperation] = useState(null);
  const [parentRouting, setParentRouting] = useState([]);
  const [removedComponents, setRemovedComponents] = useState([]);

  const fetchItemById = async (itemId) => {
    try {
      const response = await manufacturingAPI.items.get(itemId);
      return response.data;
    } catch (error) {
      console.error('Error fetching item:', error);
      return null;
    }
  };

  const findOrFetchItem = async (itemId) => {
    if (!itemId) return null;
    const localItem = items.find(item => item.id === itemId);
    if (localItem) return localItem;
    return await fetchItemById(itemId);
  };

  const loadParentRouting = async (itemId) => {
    if (!itemId) {
      setParentRouting([]);
      return;
    }
    try {
      const response = await manufacturingAPI.itemRouting.list({ item: itemId });
      const steps = response.data.results || response.data || [];
      setParentRouting(steps.sort((a, b) => a.sequence - b.sequence));
    } catch (error) {
      console.error('Error loading routing:', error);
      setParentRouting([]);
    }
  };

  useEffect(() => {
    const initializeForm = async () => {
      if (!open) return;
      setRemovedComponents([]);

      if (editingBom) {
        setLoadingItems(true);
        try {
          const [parentItem, childItem] = await Promise.all([
            findOrFetchItem(editingBom.parent_item),
            findOrFetchItem(editingBom.child_item)
          ]);

          const assemblyOperation = parentItem?.assembly_operation 
            ? operations.find(op => op.id === parentItem.assembly_operation) 
            : null;

          setOriginalAssemblyOperation(assemblyOperation);
          
          if (parentItem) {
            await loadParentRouting(parentItem.id);
          }

          setFormData({
            parent_item: parentItem,
            assembly_operation: assemblyOperation,
            effective_date: editingBom.effective_date || new Date().toISOString().split('T')[0],
            components: [{
              id: editingBom.id,
              child_item: childItem,
              quantity_required: String(parseFloat(editingBom.quantity_required)),
              sequence: editingBom.sequence || 1,
              consumed_in_step: editingBom.consumed_in_step || 1
            }]
          });
        } catch (error) {
          console.error('Error loading BOM data:', error);
          toast.error('Failed to load BOM data');
        } finally {
          setLoadingItems(false);
        }
      } else if (editingBulkBoms?.length > 0) {
        setLoadingItems(true);
        try {
          const firstBom = editingBulkBoms[0];
          const parentItem = await findOrFetchItem(firstBom.parent_item);
          const childItemPromises = editingBulkBoms.map(bom => findOrFetchItem(bom.child_item));
          const childItems = await Promise.all(childItemPromises);

          const assemblyOperation = parentItem?.assembly_operation 
            ? operations.find(op => op.id === parentItem.assembly_operation) 
            : null;

          setOriginalAssemblyOperation(assemblyOperation);
          
          if (parentItem) {
            await loadParentRouting(parentItem.id);
          }

          setFormData({
            parent_item: parentItem,
            assembly_operation: assemblyOperation,
            effective_date: firstBom.effective_date || new Date().toISOString().split('T')[0],
            components: editingBulkBoms.map((bom, index) => ({
              id: bom.id,
              child_item: childItems[index],
              quantity_required: String(parseFloat(bom.quantity_required)),
              sequence: bom.sequence || index + 1,
              consumed_in_step: bom.consumed_in_step || 1
            }))
          });
        } catch (error) {
          console.error('Error loading BOM data:', error);
          toast.error('Failed to load BOM data');
        } finally {
          setLoadingItems(false);
        }
      } else {
        setOriginalAssemblyOperation(null);
        setParentRouting([]);
        setFormData({
          parent_item: null,
          assembly_operation: null,
          effective_date: new Date().toISOString().split('T')[0],
          components: [{ id: null, child_item: null, quantity_required: '', sequence: 1, consumed_in_step: 1 }]
        });
      }
    };

    initializeForm();
  }, [open, editingBom, editingBulkBoms, items, operations]);

  useEffect(() => {
    if (formData.parent_item && !editingBom && !editingBulkBoms) {
      const assemblyOperation = formData.parent_item.assembly_operation
        ? operations.find(op => op.id === formData.parent_item.assembly_operation)
        : null;
      
      setFormData(prev => ({
        ...prev,
        assembly_operation: assemblyOperation
      }));
      setOriginalAssemblyOperation(assemblyOperation);
      loadParentRouting(formData.parent_item.id);
    }
  }, [formData.parent_item, editingBom, editingBulkBoms, operations]);

  const isEditing = editingBom || (editingBulkBoms?.length > 0);
  const totalSteps = parentRouting.length || 1;

  const handleSubmit = async () => {
    if (!formData.parent_item) {
      toast.error('Parent item is required');
      return;
    }

    const validComponents = formData.components.filter(
      comp => comp.child_item && comp.quantity_required
    );

    if (validComponents.length === 0) {
      toast.error('At least one component with item and quantity is required');
      return;
    }

    const selfRef = validComponents.find(c => c.child_item?.id === formData.parent_item.id);
    if (selfRef) {
      toast.error('A component cannot reference itself as a child');
      return;
    }

    const childIds = validComponents.map(c => c.child_item.id);
    const hasDuplicates = childIds.length !== new Set(childIds).size;
    if (hasDuplicates) {
      toast.error('Duplicate components detected. Each child item can only appear once.');
      return;
    }

    setSaving(true);
    try {
      const operationChanged = formData.assembly_operation?.id !== originalAssemblyOperation?.id;
      
      if (operationChanged) {
        await manufacturingAPI.items.update(formData.parent_item.id, {
          assembly_operation: formData.assembly_operation?.id || null
        });
      }

      // Delete removed components first
      if (removedComponents.length > 0) {
        await Promise.all(
          removedComponents.map(bomId => manufacturingAPI.bom.delete(bomId))
        );
      }

      const promises = validComponents.map(comp => {
        const submitData = {
          parent_item: formData.parent_item.id,
          child_item: comp.child_item.id,
          quantity_required: parseFloat(comp.quantity_required),
          sequence: parseInt(comp.sequence) || 1,
          consumed_in_step: parseInt(comp.consumed_in_step) || 1,
          effective_date: formData.effective_date,
          is_active: true
        };

        return comp.id
          ? manufacturingAPI.bom.update(comp.id, submitData)
          : manufacturingAPI.bom.create(submitData);
      });

      await Promise.all(promises);

      const created = validComponents.filter(c => !c.id).length;
      const updated = validComponents.filter(c => c.id).length;
      const deleted = removedComponents.length;

      let message = '';
      if (created && updated) {
        message = `Created ${created} and updated ${updated} BOM relationships`;
      } else if (created) {
        message = `Created ${created} BOM relationship${created > 1 ? 's' : ''}`;
      } else if (updated) {
        message = `Updated ${updated} BOM relationship${updated > 1 ? 's' : ''}`;
      }
      if (deleted) {
        message += `${message ? ', ' : ''}Deleted ${deleted} BOM relationship${deleted > 1 ? 's' : ''}`;
      }
      
      if (operationChanged) {
        message += ` (assembly operation ${formData.assembly_operation ? 'updated' : 'cleared'})`;
      }
      
      toast.success(message);

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving BOM:', error);
      const detail = error.response?.data?.assembly_operation?.[0]
        || error.response?.data?.detail;
      toast.error(detail ? `Failed to save BOM: ${detail}` : 'Failed to save BOM');
    } finally {
      setSaving(false);
    }
  };

  const addComponent = () => {
    setFormData(prev => ({
      ...prev,
      components: [
        ...prev.components,
        { 
          id: null, 
          child_item: null, 
          quantity_required: '', 
          sequence: prev.components.length + 1,
          consumed_in_step: 1
        }
      ]
    }));
  };

  const removeComponent = (index) => {
    if (formData.components.length > 1) {
      const removed = formData.components[index];
      if (removed?.id) {
        setRemovedComponents(prev => [...prev, removed.id]);
      }
      setFormData(prev => ({
        ...prev,
        components: prev.components.filter((_, i) => i !== index)
      }));
    }
  };

  const handleComponentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.map((comp, i) =>
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const excludedItemIds = useMemo(() => {
    const ids = formData.components
      .filter(c => c.child_item)
      .map(c => c.child_item.id);
    if (formData.parent_item) {
      ids.push(formData.parent_item.id);
    }
    return ids;
  }, [formData.parent_item, formData.components]);

  const dialogTitle = editingBom
    ? 'Edit BOM Component'
    : editingBulkBoms?.length > 0
    ? `Edit BOM: ${editingBulkBoms.length} Components`
    : 'Create BOM';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {dialogTitle}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loadingItems ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading BOM data...</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={5}>
                <EnhancedItemSelector
                  label="Parent Item (What you're making)"
                  value={formData.parent_item}
                  onChange={(value) => setFormData(prev => ({ ...prev, parent_item: value }))}
                  helperText="The assembled product"
                  required
                  disabled={isEditing}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={operations}
                  getOptionLabel={(option) => `${option.operation_code} - ${option.name}`}
                  value={formData.assembly_operation}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, assembly_operation: value }))}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  disabled={parentRouting.length > 1}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assembly Operation"
                      helperText={
                        parentRouting.length > 1
                          ? 'Multi-step item — change operations via the routing editor'
                          : formData.parent_item
                          ? `How to assemble ${formData.parent_item.item_code}`
                          : "Select parent item first"
                      }
                    />
                  )}
                />
                {formData.assembly_operation?.id !== originalAssemblyOperation?.id && formData.parent_item && (
                  <Alert severity="info" sx={{ mt: 1 }} icon={<BuildIcon fontSize="small" />}>
                    This will update the assembly operation for <strong>{formData.parent_item.item_code}</strong>
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Effective Date"
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Show routing info if available */}
            {parentRouting.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Routing Steps:</strong>
                </Typography>
                <RoutingStepFlow routingSteps={parentRouting} onManageRouting={() => {}} />
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Components ({formData.components.length})
              </Typography>
              <Button 
                size="small" 
                startIcon={<AddCircleIcon />} 
                onClick={addComponent}
                variant="outlined"
              >
                Add Component
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                px: 1,
                py: 0.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
                mb: 1
              }}
            >
              <Box sx={{ flex: 5 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  CHILD ITEM *
                </Typography>
              </Box>
              <Box sx={{ width: 90 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  QTY *
                </Typography>
              </Box>
              <Box sx={{ width: 70 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  SEQ
                </Typography>
              </Box>
              {totalSteps > 1 && (
                <Box sx={{ width: 80 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    STEP
                  </Typography>
                </Box>
              )}
              <Box sx={{ width: 40 }} />
            </Box>

            {formData.components.map((component, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 5 }}>
                    <EnhancedItemSelector
                      label=""
                      value={component.child_item}
                      onChange={(value) => handleComponentChange(index, 'child_item', value)}
                      size="small"
                      excludeItems={excludedItemIds.filter(id => id !== component.child_item?.id)}
                      required
                    />
                    {component.child_item && (
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {component.child_item.name}
                        </Typography>
                        {component.child_item.is_phantom && (
                          <Chip
                            icon={<PhantomIcon sx={{ fontSize: 10 }} />}
                            label="Phantom"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 16, fontSize: '0.6rem' }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ width: 90 }}>
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="Qty"
                      value={component.quantity_required}
                      onChange={(e) => handleComponentChange(index, 'quantity_required', e.target.value)}
                      inputProps={{ step: "0.01", min: "0.01" }}
                      required
                    />
                  </Box>
                  <Box sx={{ width: 70 }}>
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="Seq"
                      value={component.sequence}
                      onChange={(e) => handleComponentChange(index, 'sequence', e.target.value)}
                      inputProps={{ min: "1" }}
                    />
                  </Box>
                  {totalSteps > 1 && (
                    <Box sx={{ width: 80 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={component.consumed_in_step || 1}
                          onChange={(e) => handleComponentChange(index, 'consumed_in_step', e.target.value)}
                        >
                          {parentRouting.map((step) => (
                            <MenuItem key={step.sequence} value={step.sequence}>
                              {step.sequence}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  <Box sx={{ width: 40 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeComponent(index)}
                      disabled={formData.components.length === 1}
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}

            {formData.components.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Add at least one component to create a BOM relationship.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loadingItems || !formData.parent_item || formData.components.every(c => !c.child_item || !c.quantity_required)}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : isEditing ? 'Update BOM' : 'Create BOM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// BOM EXPLOSION DIALOG
// ============================================================================

const BOMExplosionDialog = ({ open, onClose, loading, results }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      BOM Explosion & Bottleneck Analysis
    </DialogTitle>
    <DialogContent dividers>
      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Analyzing requirements...</Typography>
        </Box>
      ) : results ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            {results.item} - Qty: {results.quantity_requested}
          </Typography>

          {results.total_bottlenecks > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{results.total_bottlenecks} bottleneck(s)</strong> - Materials needed before production.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              All materials available - production can proceed!
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Item</TableCell>
                  <TableCell align="center">Step</TableCell>
                  <TableCell align="right">Required</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell align="right">Shortage</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.requirements?.map((req, index) => (
                  <TableRow
                    key={index}
                    sx={{ bgcolor: req.is_bottleneck ? 'error.50' : 'inherit' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {req.item_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {req.item_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {req.consumed_in_step && (
                        <StepBadge step={req.consumed_in_step} totalSteps={results.total_steps || 1} />
                      )}
                    </TableCell>
                    <TableCell align="right">{req.required_quantity}</TableCell>
                    <TableCell align="right">{req.available_quantity}</TableCell>
                    <TableCell align="right">
                      {req.shortage > 0 ? (
                        <Typography color="error" fontWeight="bold">{req.shortage}</Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {req.is_bottleneck ? (
                        <Chip icon={<WarningIcon />} label="Shortage" color="error" size="small" />
                      ) : (
                        <Chip label="OK" color="success" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

// ============================================================================
// DUPLICATE BOM DIALOG
// ============================================================================

const ITEM_TYPES = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'manufactured', label: 'Manufactured Part' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'finished_good', label: 'Finished Good' },
];

const DuplicateBOMDialog = ({ open, onClose, sourceItem, onSuccess }) => {
  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('finished_good');
  const [isPhantom, setIsPhantom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeValid, setCodeValid] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Pre-fill when source item changes
  useEffect(() => {
    if (open && sourceItem) {
      setItemCode(`${sourceItem.item_code || ''}-COPY`);
      setItemName(`${sourceItem.name || ''} (Copy)`);
      setDescription(sourceItem.description || '');
      setItemType(sourceItem.item_type || 'finished_good');
      setIsPhantom(sourceItem.is_phantom || false);
      setCodeError('');
      setCodeValid(false);
      setSaving(false);
    }
  }, [open, sourceItem]);

  // Debounced validation of item code
  useEffect(() => {
    if (!open) return;
    if (!itemCode.trim()) {
      setCodeError('');
      setCodeValid(false);
      return;
    }

    const codeUpper = itemCode.trim().toUpperCase();

    if (sourceItem && codeUpper === (sourceItem.item_code || '').toUpperCase()) {
      setCodeError('Code must be different from the original item');
      setCodeValid(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsValidating(true);
      try {
        const response = await manufacturingAPI.items.list({
          search: itemCode.trim(),
          page_size: 10,
        });
        const items = response.data.results || response.data;
        const exactMatch = items.find(
          item => item.item_code.toUpperCase() === codeUpper
        );
        if (exactMatch) {
          setCodeError(`Item code "${exactMatch.item_code}" already exists`);
          setCodeValid(false);
        } else {
          setCodeError('');
          setCodeValid(true);
        }
      } catch (err) {
        console.error('Failed to validate item code:', err);
        setCodeError('');
        setCodeValid(true);
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [itemCode, sourceItem, open]);

  const isFormValid = itemCode.trim() && itemName.trim() && codeValid && !codeError && !isValidating && !saving;

  const handleSubmit = async () => {
    if (!isFormValid || !sourceItem) return;

    setSaving(true);
    try {
      const response = await manufacturingAPI.bom.duplicateBOM({
        source_item_id: sourceItem.id,
        new_item_code: itemCode.trim().toUpperCase(),
        new_item_name: itemName.trim(),
        new_item_description: description.trim(),
        new_item_type: itemType,
        new_is_phantom: isPhantom,
      });

      const data = response.data;
      toast.success(
        `Duplicated BOM: ${data.bom_lines_copied} component(s) and ${data.routing_steps_copied} routing step(s) copied to ${data.new_item.item_code}`
      );
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to duplicate BOM:', err);
      const detail = err.response?.data?.error || err.response?.data?.detail || err.message;
      toast.error(`Failed to duplicate BOM: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Duplicate BOM
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {sourceItem && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Creating a copy of <strong>{sourceItem.item_code}</strong> — {sourceItem.name}.
            Child components will be shared (same items), only the parent is new.
          </Alert>
        )}

        <TextField
          fullWidth
          label="New Item Code"
          required
          value={itemCode}
          onChange={(e) => setItemCode(e.target.value.toUpperCase())}
          placeholder="e.g. PRODUCT-NEW"
          autoFocus
          error={!!codeError}
          helperText={
            codeError || (isValidating ? 'Checking...' : codeValid ? 'Item code is available' : '')
          }
          color={codeValid && !codeError ? 'success' : undefined}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              endAdornment: isValidating ? <CircularProgress size={16} /> : null,
            }
          }}
        />

        <TextField
          fullWidth
          label="Item Name"
          required
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
          placeholder="e.g. New Product Assembly"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Item Type</InputLabel>
          <Select
            value={itemType}
            label="Item Type"
            onChange={(e) => setItemType(e.target.value)}
          >
            {ITEM_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch checked={isPhantom} onChange={(e) => setIsPhantom(e.target.checked)} />
          }
          label="Phantom Item (components explode through to parent)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid}
          startIcon={saving ? <CircularProgress size={16} /> : <ContentCopyIcon />}
        >
          {saving ? 'Duplicating...' : 'Duplicate BOM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// MAIN BOM MANAGEMENT COMPONENT
// ============================================================================

function BOMManagement() {
  const {
    items,
    operations,
    itemsLoading,
    operationsLoading,
    loadItems,
    loadOperations
  } = useManufacturingData();

  const [boms, setBoms] = useState([]);
  const [allBoms, setAllBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  const [openDialog, setOpenDialog] = useState(false);
  const [openExplosionDialog, setOpenExplosionDialog] = useState(false);
  const [openRoutingDialog, setOpenRoutingDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [duplicateSourceItem, setDuplicateSourceItem] = useState(null);
  const [routingItem, setRoutingItem] = useState(null);

  usePageTitle(searchTerm ? `BOM: ${searchTerm}` : 'BOM');

  const [editingBom, setEditingBom] = useState(null);
  const [editingBulkBoms, setEditingBulkBoms] = useState(null);
  const [explosionResults, setExplosionResults] = useState(null);
  const [explosionLoading, setExplosionLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Store routing data for each parent item
  const [itemRoutings, setItemRoutings] = useState({});

  const requestIdRef = useRef(0);
  const initialLoadRef = useRef(true);

  const [stats, setStats] = useState({
    totalBoms: 0,
    parentItems: 0,
    finishedProducts: 0,
    assemblies: 0
  });

  const rootParents = useMemo(() => computeRootParents(allBoms), [allBoms]);
  const itemsWithChildren = useMemo(() => getItemsWithChildren(allBoms), [allBoms]);

  useEffect(() => {
    if (!items.length && !itemsLoading) loadItems();
    if (!operations.length && !operationsLoading) loadOperations();
  }, [items.length, operations.length, itemsLoading, operationsLoading, loadItems, loadOperations]);

  // Ref mirror so the cache check inside memoized callbacks (loadData) never
  // reads a stale itemRoutings snapshot — a stale hit here used to skip the
  // refetch after saving the Routing dialog, leaving cards on old operations.
  const itemRoutingsRef = useRef(itemRoutings);
  useEffect(() => {
    itemRoutingsRef.current = itemRoutings;
  }, [itemRoutings]);

  const loadRoutingForItems = useCallback(async (itemIds, { force = false } = {}) => {
    const uniqueIds = [...new Set(itemIds)].filter(
      id => id && (force || !itemRoutingsRef.current[id])
    );
    if (uniqueIds.length === 0) return;

    try {
      const routingPromises = uniqueIds.map(id => 
        manufacturingAPI.itemRouting.list({ item: id })
          .then(res => ({ id, steps: res.data.results || res.data || [] }))
          .catch(() => ({ id, steps: [] }))
      );
      
      const results = await Promise.all(routingPromises);
      const newRoutings = {};
      results.forEach(({ id, steps }) => {
        newRoutings[id] = steps.sort((a, b) => a.sequence - b.sequence);
      });
      
      setItemRoutings(prev => ({ ...prev, ...newRoutings }));
    } catch (error) {
      console.error('Error loading routings:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    if (itemsLoading || operationsLoading) return;

    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    try {
      const params = { page: currentPage, page_size: pageSize };
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await manufacturingAPI.bom.withChildren(params);

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const pageBoms = response.data.page_boms || [];
      setBoms(pageBoms);
      setAllBoms(response.data.all_boms || []);
      setTotalCount(response.data.count || 0);
      initialLoadRef.current = false;

      // Load routing for all parent items
      const parentItemIds = [...new Set(pageBoms.map(bom => bom.parent_item))];
      loadRoutingForItems(parentItemIds);

      const uniqueParents = new Set(pageBoms.map(bom => bom.parent_item_display));
      setStats({
        totalBoms: response.data.count || 0,
        parentItems: uniqueParents.size,
        finishedProducts: items.filter(i => i.item_type === 'finished_good').length,
        assemblies: items.filter(i => i.item_type === 'assembly').length
      });
    } catch (error) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      console.error('Error loading BOM data:', error);
      toast.error('Failed to load BOM data');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [searchTerm, currentPage, itemsLoading, operationsLoading, items, loadRoutingForItems]);

  useEffect(() => {
    if (itemsLoading || operationsLoading) return;

    if (initialLoadRef.current) {
      loadData();
    } else {
      const timeout = setTimeout(() => {
        loadData();
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [loadData, itemsLoading, operationsLoading]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'n') {
          e.preventDefault();
          handleOpenDialog();
        } else if (e.key === 'f') {
          e.preventDefault();
          document.getElementById('bom-search')?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleOpenDialog = (bom = null) => {
    setEditingBom(bom);
    setEditingBulkBoms(null);
    setOpenDialog(true);
  };

  const handleOpenBulkDialog = (bomItems) => {
    setEditingBom(null);
    setEditingBulkBoms(bomItems);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBom(null);
    setEditingBulkBoms(null);
  };

  const handleDelete = async (bom) => {
    if (window.confirm('Delete this BOM relationship?')) {
      try {
        await manufacturingAPI.bom.delete(bom.id);
        toast.success('BOM deleted');
        loadData();
      } catch (error) {
        console.error('Error deleting BOM:', error);
        toast.error('Failed to delete BOM');
      }
    }
  };

  const handleExplode = async (item, quantity = 1) => {
    if (!item) {
      toast.error('Item not found');
      return;
    }

    setExplosionLoading(true);
    setOpenExplosionDialog(true);

    try {
      const response = await manufacturingAPI.bom.explodeRequirements({
        item_id: item.id,
        quantity
      });
      setExplosionResults(response.data);
    } catch (error) {
      console.error('Error exploding BOM:', error);
      toast.error('Failed to explode BOM');
      setOpenExplosionDialog(false);
    } finally {
      setExplosionLoading(false);
    }
  };

  const handleCardToggle = (parentKey) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(parentKey)) {
      newExpanded.delete(parentKey);
    } else {
      newExpanded.add(parentKey);
    }
    setExpandedCards(newExpanded);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    setSearchTerm('');
    setExpandedCards(new Set());
    setItemRoutings({});
    loadData();
  };

  const handleSearchChange = (e) => {
    setCurrentPage(1);
    setSearchTerm(e.target.value);
  };

  const handleEditAsParent = (childItemId, childItemDisplay) => {
    const bomsAsParent = allBoms.filter(bom => bom.parent_item_display === childItemDisplay);
    
    if (bomsAsParent.length > 0) {
      handleOpenBulkDialog(bomsAsParent);
    } else {
      toast.warning('No BOM found for this item as parent');
    }
  };

  const handleManageRouting = async (item) => {
    if (!item) {
      toast.error('Item not found');
      return;
    }
    
    // If item doesn't have full data, fetch it
    if (!item.item_code) {
      try {
        const response = await manufacturingAPI.items.get(item.id || item);
        setRoutingItem(response.data);
      } catch (error) {
        console.error('Error fetching item:', error);
        toast.error('Failed to load item details');
        return;
      }
    } else {
      setRoutingItem(item);
    }
    setOpenRoutingDialog(true);
  };

  const handleRoutingSaved = () => {
    // Force-refetch this item's routing (bypasses the cache check) and reload
    if (routingItem) {
      loadRoutingForItems([routingItem.id], { force: true });
    }
    loadData();
  };

  const handleTogglePhantom = async (itemId, isPhantom) => {
    try {
      await manufacturingAPI.items.update(itemId, { is_phantom: isPhantom });
      toast.success(`Item ${isPhantom ? 'marked as phantom' : 'unmarked as phantom'}`);
      loadData();
    } catch (error) {
      console.error('Error toggling phantom:', error);
      toast.error('Failed to update phantom status');
    }
  };

  const handleUpdateStep = async (bomItem, newStep) => {
    try {
      await manufacturingAPI.bom.update(bomItem.id, { consumed_in_step: newStep });
      toast.success(`Component moved to Step ${newStep}`);
      loadData();
    } catch (error) {
      console.error('Error updating step:', error);
      toast.error('Failed to update step assignment');
    }
  };

  const handleDuplicate = (sourceItem) => {
    setDuplicateSourceItem(sourceItem);
    setOpenDuplicateDialog(true);
  };

  const handleDuplicateSuccess = () => {
    loadItems();
    loadData();
  };

  const groupedBoms = useMemo(() => {
    return boms.reduce((acc, bom) => {
      const key = bom.parent_item_display || `Unknown-${bom.parent_item}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(bom);
      return acc;
    }, {});
  }, [boms]);

  const groupedBomsArray = Object.entries(groupedBoms)
    .filter(([parentKey]) => {
      if (searchTerm.trim()) {
        return true;
      }
      return rootParents.has(parentKey);
    })
    .sort((a, b) => a[0].localeCompare(b[0]));

  const totalPages = Math.ceil(totalCount / pageSize);

  const explodableItems = items.filter(
    i => i.item_type === 'finished_good' || i.item_type === 'assembly'
  );

  if (loading && initialLoadRef.current) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TreeIcon color="primary" />
            BOM Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} disabled>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} disabled>
              Create BOM
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 28, height: 28, bgcolor: 'grey.200', borderRadius: 1 }} />
                    <Box>
                      <Box sx={{ width: 60, height: 12, bgcolor: 'grey.200', borderRadius: 1, mb: 0.5 }} />
                      <Box sx={{ width: 40, height: 24, bgcolor: 'grey.200', borderRadius: 1 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Search by Parent Item"
              placeholder="Item code or name..."
              disabled
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.disabled' }} /> }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading BOM data...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TreeIcon color="primary" />
          BOM Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Create BOM
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatsCard title="Total BOMs" value={stats.totalBoms} icon={TreeIcon} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard title="Parent Items" value={stats.parentItems} color="secondary" icon={CategoryIcon} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard title="Finished Products" value={stats.finishedProducts} color="success" icon={InventoryIcon} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard title="Assemblies" value={stats.assemblies} color="warning" icon={BuildIcon} />
        </Grid>
      </Grid>

      {explodableItems.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={explodableItems}
                  getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Stack spacing={0}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {option.item_code}
                            </Typography>
                            {option.is_phantom && (
                              <Chip
                                icon={<PhantomIcon sx={{ fontSize: 12 }} />}
                                label="Phantom"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {option.name}
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Quick BOM Explosion" size="small" placeholder="Select a product..." />
                  )}
                  onChange={(e, value) => value && handleExplode(value, 1)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" color="text.secondary">
                  Select a finished product or assembly to analyze material requirements and identify bottlenecks.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              id="bom-search"
              fullWidth
              size="small"
              label="Search by Parent Item"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Item code or name..."
              autoComplete="off"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
            />
            {searchTerm && (
              <Button variant="outlined" size="small" onClick={() => { setCurrentPage(1); setSearchTerm(''); }}>
                Clear
              </Button>
            )}
          </Box>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {searchTerm
                ? `Found ${totalCount} BOMs matching "${searchTerm}"`
                : `Showing ${boms.length} of ${totalCount} BOMs`}
            </Typography>
            {loading && <CircularProgress size={16} />}
          </Box>
        </CardContent>
      </Card>

      {groupedBomsArray.length > 0 ? (
        <>
          {groupedBomsArray.map(([parentKey, bomItems]) => {
            const parentItem = items.find(i => i.id === bomItems[0]?.parent_item);
            const isRootParent = rootParents.has(parentKey);
            const parentId = bomItems[0]?.parent_item;
            const routingSteps = itemRoutings[parentId] || [];
            
            return (
              <ParentBOMCard
                key={parentKey}
                parentKey={parentKey}
                bomItems={bomItems}
                allBoms={allBoms}
                itemsWithChildren={itemsWithChildren}
                isRootParent={isRootParent}
                parentItem={parentItem}
                isExpanded={expandedCards.has(parentKey)}
                onToggle={() => handleCardToggle(parentKey)}
                onEdit={handleOpenDialog}
                onEditBulk={handleOpenBulkDialog}
                onDelete={handleDelete}
                onExplode={handleExplode}
                onEditAsParent={handleEditAsParent}
                onManageRouting={handleManageRouting}
                onTogglePhantom={handleTogglePhantom}
                onDuplicate={handleDuplicate}
                onUpdateStep={handleUpdateStep}
                routingSteps={routingSteps}
              />
            );
          })}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <TreeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? `No BOMs found for "${searchTerm}"` : 'No BOMs found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchTerm
                ? 'Try a different search term or clear the filter.'
                : 'Create BOM relationships to define how products are built.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              {searchTerm && (
                <Button variant="outlined" onClick={() => { setCurrentPage(1); setSearchTerm(''); }}>
                  Clear Search
                </Button>
              )}
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                Create BOM
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <BOMDialog
        open={openDialog}
        onClose={handleCloseDialog}
        editingBom={editingBom}
        editingBulkBoms={editingBulkBoms}
        onSave={loadData}
        items={items}
        operations={operations}
      />

      <BOMExplosionDialog
        open={openExplosionDialog}
        onClose={() => setOpenExplosionDialog(false)}
        loading={explosionLoading}
        results={explosionResults}
      />

      <RoutingDialog
        open={openRoutingDialog}
        onClose={() => {
          setOpenRoutingDialog(false);
          setRoutingItem(null);
        }}
        item={routingItem}
        operations={operations}
        onSave={handleRoutingSaved}
      />

      <DuplicateBOMDialog
        open={openDuplicateDialog}
        onClose={() => {
          setOpenDuplicateDialog(false);
          setDuplicateSourceItem(null);
        }}
        sourceItem={duplicateSourceItem}
        onSuccess={handleDuplicateSuccess}
      />
    </Box>
  );
}

export default BOMManagement;